import api, { unwrap } from './client';
import type { ApiResponse } from '@/types/api';

// ——— Types ———

export interface MarketingCampaign {
  id: number;
  campaignName: string;
  description?: string;
  channel: string;
  campaignType?: string;
  lifecycleStatus: string;
  segmentId?: number;
  sharedTemplateId?: number;
  scheduledAt?: string;
  approvalStatus?: string;
  budget?: number;
  cost?: number;
  revenueAttributed?: number;
}

export interface MarketingSegment {
  id: number;
  segmentName: string;
  segmentType: string;
  rulesJson?: string;
  estimatedCount: number;
}

export interface MarketingJourney {
  id: number;
  journeyName: string;
  triggerEvent: string;
  journeyStatus: string;
  canvasJson?: string;
}

export interface MarketingAbTest {
  id: number;
  campaignId: number;
  testType: string;
  variantARef?: string;
  variantBRef?: string;
  winnerVariant?: string;
  testStatus: string;
  startedAt?: string;
  endedAt?: string;
}

export interface JourneyCanvas {
  nodes: { key: string; type: string; label: string; x?: number; y?: number }[];
  edges: { from: string; to: string }[];
}

export interface SharedTemplate {
  id: number;
  templateCode?: string;
  templateName: string;
  channel: string;
  subject?: string;
  bodyHtml?: string;
  bodyText?: string;
  published?: boolean;
  version?: number;
}

export interface MarketingDashboard {
  totalCampaigns: number;
  totalSent: number;
  totalOpens: number;
  totalClicks: number;
  totalConversions: number;
  openRate: number;
  clickRate: number;
  conversionRate: number;
  topCampaigns: { campaignId: number; name?: string; opens: number }[];
}

export interface SegmentRule {
  field: string;
  operator: string;
  value: string | number;
}

export interface SegmentRulesPayload {
  matchMode: 'AND' | 'OR';
  groups: SegmentRule[];
}

export interface ResolvedMember {
  entityType: string;
  entityId: number;
  email?: string;
}

export interface LifecyclePreset {
  code: string;
  name: string;
  channel: string;
}

export type CampaignPerformance = Record<string, number>;

// ——— API ———

export const marketingApi = {
  // Analytics
  getDashboard: () =>
    unwrap<MarketingDashboard>(api.get<ApiResponse<MarketingDashboard>>('/v1/marketing/analytics/dashboard')),

  // Campaigns
  listCampaigns: () =>
    unwrap<MarketingCampaign[]>(api.get<ApiResponse<MarketingCampaign[]>>('/v1/marketing/campaigns')),

  getCampaign: (id: number) =>
    unwrap<MarketingCampaign>(api.get<ApiResponse<MarketingCampaign>>(`/v1/marketing/campaigns/${id}`)),

  createCampaign: (body: Partial<MarketingCampaign>) =>
    unwrap<MarketingCampaign>(api.post<ApiResponse<MarketingCampaign>>('/v1/marketing/campaigns', body)),

  updateCampaign: (id: number, body: Partial<MarketingCampaign>) =>
    unwrap<MarketingCampaign>(api.put<ApiResponse<MarketingCampaign>>(`/v1/marketing/campaigns/${id}`, body)),

  submitApproval: (id: number) =>
    unwrap<MarketingCampaign>(api.post<ApiResponse<MarketingCampaign>>(`/v1/marketing/campaigns/${id}/submit-approval`)),

  approveCampaign: (id: number) =>
    unwrap<MarketingCampaign>(api.post<ApiResponse<MarketingCampaign>>(`/v1/marketing/campaigns/${id}/approve`)),

  cloneCampaign: (id: number) =>
    unwrap<MarketingCampaign>(api.post<ApiResponse<MarketingCampaign>>(`/v1/marketing/campaigns/${id}/clone`)),

  scheduleCampaign: (id: number, scheduledAt: string) =>
    unwrap<MarketingCampaign>(api.post<ApiResponse<MarketingCampaign>>(`/v1/marketing/campaigns/${id}/schedule`, {
      scheduledAt,
    })),

  archiveCampaign: (id: number) =>
    unwrap<void>(api.post<ApiResponse<void>>(`/v1/marketing/campaigns/${id}/archive`)),

  startCampaign: (id: number) =>
    unwrap<MarketingCampaign>(api.post<ApiResponse<MarketingCampaign>>(`/v1/marketing/campaigns/${id}/start`)),

  completeCampaign: (id: number) =>
    unwrap<MarketingCampaign>(api.post<ApiResponse<MarketingCampaign>>(`/v1/marketing/campaigns/${id}/complete`)),

  getPerformance: (id: number) =>
    unwrap<CampaignPerformance>(
      api.get<ApiResponse<CampaignPerformance>>(`/v1/marketing/campaigns/${id}/performance`)
    ),

  getRoi: (id: number) =>
    unwrap<Record<string, unknown>>(api.get<ApiResponse<Record<string, unknown>>>(`/v1/marketing/campaigns/${id}/roi`)),

  // Segments
  listSegments: () =>
    unwrap<MarketingSegment[]>(api.get<ApiResponse<MarketingSegment[]>>('/v1/marketing/segments')),

  createSegment: (body: Partial<MarketingSegment>) =>
    unwrap<MarketingSegment>(api.post<ApiResponse<MarketingSegment>>('/v1/marketing/segments', body)),

  refreshSegment: (id: number) =>
    unwrap<MarketingSegment>(api.post<ApiResponse<MarketingSegment>>(`/v1/marketing/segments/${id}/refresh`)),

  previewSegment: (segmentType: string, rulesJson: string) =>
    unwrap<ResolvedMember[]>(
      api.post<ApiResponse<ResolvedMember[]>>('/v1/marketing/segments/preview', { segmentType, rulesJson })
    ),

  // Templates
  listTemplates: () =>
    unwrap<SharedTemplate[]>(api.get<ApiResponse<SharedTemplate[]>>('/v1/marketing/templates')),

  createTemplate: (body: Partial<SharedTemplate>) =>
    unwrap<SharedTemplate>(
      api.post<ApiResponse<SharedTemplate>>('/v1/marketing/templates', { ...body, ownerModule: 'MARKETING' })
    ),

  // Journeys
  listJourneys: () =>
    unwrap<MarketingJourney[]>(api.get<ApiResponse<MarketingJourney[]>>('/v1/marketing/journeys')),

  createJourney: (body: Partial<MarketingJourney>) =>
    unwrap<MarketingJourney>(api.post<ApiResponse<MarketingJourney>>('/v1/marketing/journeys', body)),

  submitJourneyApproval: (id: number) =>
    unwrap<MarketingJourney>(api.post<ApiResponse<MarketingJourney>>(`/v1/marketing/journeys/${id}/submit-approval`)),

  activateJourney: (id: number) =>
    unwrap<MarketingJourney>(api.post<ApiResponse<MarketingJourney>>(`/v1/marketing/journeys/${id}/activate`)),

  updateJourney: (id: number, body: Partial<MarketingJourney>) =>
    unwrap<MarketingJourney>(api.put<ApiResponse<MarketingJourney>>(`/v1/marketing/journeys/${id}`, body)),

  listAbTests: (campaignId: number) =>
    unwrap<MarketingAbTest[]>(
      api.get<ApiResponse<MarketingAbTest[]>>(`/v1/marketing/campaigns/${campaignId}/ab-tests`),
    ),

  createAbTest: (campaignId: number, body: Partial<MarketingAbTest>) =>
    unwrap<MarketingAbTest>(
      api.post<ApiResponse<MarketingAbTest>>(`/v1/marketing/campaigns/${campaignId}/ab-tests`, body),
    ),

  pickAbWinner: (testId: number) =>
    unwrap<MarketingAbTest>(api.post<ApiResponse<MarketingAbTest>>(`/v1/marketing/ab-tests/${testId}/pick-winner`)),

  // Lifecycle
  listLifecyclePresets: () =>
    unwrap<LifecyclePreset[]>(api.get<ApiResponse<LifecyclePreset[]>>('/v1/marketing/lifecycle/presets')),

  createLifecycleCampaign: (code: string, segmentId?: number) =>
    unwrap<MarketingCampaign>(
      api.post<ApiResponse<MarketingCampaign>>(`/v1/marketing/lifecycle/presets/${code}`, null, {
        params: segmentId ? { segmentId } : undefined,
      })
    ),

  // Reports
  exportCampaignsCsv: () => api.get('/v1/marketing/reports/campaigns/export.csv', { responseType: 'text' }),

  exportJourneysCsv: () => api.get('/v1/marketing/reports/journeys/export.csv', { responseType: 'text' }),

  getAttribution: (campaignId: number, model?: string) =>
    unwrap<Record<string, unknown>>(
      api.get<ApiResponse<Record<string, unknown>>>(`/v1/marketing/reports/campaigns/${campaignId}/attribution`, {
        params: model ? { model } : undefined,
      })
    ),
};

